import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import {
  Table,
  Tabs,
  Segmented,
  Select,
  Input,
  DatePicker,
  Tooltip as AntTooltip,
  Empty,
  Skeleton,
  Drawer,
  Modal,
  Descriptions,
  Button as AntButton,
  Progress,
} from 'antd';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {
  RefreshCw,
  Download,
  TrendingUp,
  Wallet,
  Repeat,
  CalendarClock,
  Users,
  AlertTriangle,
  CreditCard,
  ExternalLink,
  Building2,
  ShieldAlert,
} from 'lucide-react';

import { useApi } from '../../hooks/useApi';
import { useTheme } from '../../contexts/ThemeContext';
import { getAdminDesignSystem, ADMIN_ROOT, AdminThemeProvider, getAdminTokens } from './adminDesignSystem';
import AdminSidebar from './AdminSidebar';

/**
 * ============================================================================
 *  REVENUE — the one page that shows every rupee and dollar, end to end.
 * ----------------------------------------------------------------------------
 *  Backed by GET /api/v1/admin/revenue/* which merges three money sources into
 *  a single ledger: Stripe (USD), Razorpay (INR), and "manual" recurring value
 *  for orgs that renew by one-time payment with no gateway subscription.
 *
 *  Layout
 *    • Header      range picker · currency toggle · refresh · CSV export
 *    • KPI strip   MRR / ARR / collected / net / upcoming / at-risk
 *    • Overview    revenue trend, MRR composition, plan mix, risk panel
 *    • Payments    unified history, server-paginated, click a row for detail
 *    • Subscriptions  every subscription incl. manual, with MRR contribution
 *    • Upcoming    renewal calendar + whether it will actually auto-collect
 *    • Customers   revenue per org (LTV, MRR, last payment)
 *    • Settlements Razorpay payouts that reached the bank
 *
 *  Currency: every number arrives pre-converted to USD *and* INR, so the
 *  toggle is instant and never re-fetches. The FX rate used is shown in the
 *  footer so nobody mistakes a converted figure for a booked one.
 * ============================================================================
 */

const API = '/api/v1/admin/revenue';

/* ---------------------------------------------------------------- formatting */

/**
 * Format money for display.
 *
 * Compact form follows the *reader's* numbering system, not a single global
 * one: rupees abbreviate as k / L (lakh) / Cr (crore), dollars as k / M.
 * Showing "₹5.0M" to someone reading a rupee figure is simply wrong, and INR
 * is this page's default currency. Full (non-compact) figures use en-IN
 * grouping for rupees too, so ₹745389.9 reads ₹7,45,389.90.
 */
const money = (value, currency, { compact = false, decimals } = {}) => {
  const n = Number(value) || 0;
  const symbol = currency === 'INR' ? '₹' : '$';
  const abs = Math.abs(n);
  if (compact) {
    if (currency === 'INR') {
      if (abs >= 1e7) return `${symbol}${(n / 1e7).toFixed(2)}Cr`;
      if (abs >= 1e5) return `${symbol}${(n / 1e5).toFixed(2)}L`;
    } else if (abs >= 1e6) {
      return `${symbol}${(n / 1e6).toFixed(2)}M`;
    }
    if (abs >= 1000) return `${symbol}${(n / 1000).toFixed(1)}k`;
  }
  const dp = decimals != null ? decimals : abs >= 1000 ? 0 : 2;
  return `${symbol}${n.toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;
};

/** Pick the pre-converted field for the active display currency. */
const pick = (row, base, currency) =>
  currency === 'INR' ? row?.[`${base}Inr`] ?? 0 : row?.[`${base}Usd`] ?? 0;

/** True when a request was cancelled because a newer one superseded it. */
const wasAborted = (e) =>
  e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError' || e?.message === 'canceled';

const dateText = (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—');
const dateTimeText = (v) => (v ? dayjs(v).format('DD MMM YYYY, HH:mm') : '—');

const STATUS_TONE = {
  succeeded: { bg: 'rgba(26,163,99,.14)', fg: '#1aa363', label: 'Succeeded' },
  partially_refunded: { bg: 'rgba(224,160,32,.16)', fg: '#c07d17', label: 'Part. refunded' },
  refunded: { bg: 'rgba(224,160,32,.16)', fg: '#c07d17', label: 'Refunded' },
  pending: { bg: 'rgba(47,111,237,.14)', fg: '#2f6fed', label: 'Pending' },
  failed: { bg: 'rgba(229,81,75,.14)', fg: '#e5514b', label: 'Failed' },
  active: { bg: 'rgba(26,163,99,.14)', fg: '#1aa363', label: 'Active' },
  trialing: { bg: 'rgba(47,111,237,.14)', fg: '#2f6fed', label: 'Trialing' },
  past_due: { bg: 'rgba(229,81,75,.14)', fg: '#e5514b', label: 'Past due' },
  canceled: { bg: 'rgba(122,130,143,.16)', fg: '#8a8a94', label: 'Canceled' },
  paused: { bg: 'rgba(122,130,143,.16)', fg: '#8a8a94', label: 'Paused' },
  unpaid: { bg: 'rgba(229,81,75,.14)', fg: '#e5514b', label: 'Unpaid' },
  // Razorpay `created` / Stripe `incomplete`: the mandate was never
  // authenticated, so it has never charged and is not pipeline either.
  incomplete: { bg: 'rgba(122,130,143,.16)', fg: '#8a8a94', label: 'Never started' },
  incomplete_expired: { bg: 'rgba(122,130,143,.16)', fg: '#8a8a94', label: 'Expired' },
};

const Pill = ({ status }) => {
  const t = STATUS_TONE[status] || { bg: 'rgba(122,130,143,.16)', fg: '#8a8a94', label: status || '—' };
  return (
    <span
      style={{
        background: t.bg,
        color: t.fg,
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }}
    >
      {t.label}
    </span>
  );
};

const GatewayTag = ({ gateway, source }) => {
  const isRzp = gateway === 'razorpay';
  const manual = source === 'manual';
  const color = manual ? '#8a8a94' : isRzp ? '#3395ff' : '#635bff';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: color, display: 'inline-block' }} />
      {manual ? 'Manual' : isRzp ? 'Razorpay' : 'Stripe'}
    </span>
  );
};

/* ------------------------------------------------------------------- pieces */

const KpiCard = ({ icon: Icon, label, value, sub, tone = 'teal', isDark, hint, loading = false }) => {
  const tones = {
    teal: { bg: isDark ? 'rgba(0,209,193,.12)' : '#e2f7f4', fg: isDark ? '#3fe0d2' : '#00877d' },
    blue: { bg: isDark ? 'rgba(47,111,237,.14)' : '#e8f1fe', fg: isDark ? '#7aa5f7' : '#2f6fed' },
    purple: { bg: isDark ? 'rgba(107,78,240,.16)' : '#efeafe', fg: isDark ? '#a892f8' : '#6b4ef0' },
    orange: { bg: isDark ? 'rgba(232,121,43,.14)' : '#fdeee4', fg: isDark ? '#f0a066' : '#e8792b' },
    green: { bg: isDark ? 'rgba(26,163,99,.14)' : '#e6f7ef', fg: isDark ? '#3fc27f' : '#1aa363' },
    red: { bg: isDark ? 'rgba(229,81,75,.14)' : '#fdecea', fg: isDark ? '#f0736c' : '#e5514b' },
  };
  const t = tones[tone] || tones.teal;
  // The placeholder lives inside the real card wrapper and mirrors its exact
  // structure, so the box is identical before and after data arrives. An AntD
  // <Skeleton> here was a different height and made the whole strip jump.
  if (loading) {
    // The icon and label are known up front, so paint them immediately and
    // shimmer only the numbers. Nothing pops in, nothing changes size.
    return (
      <div className="tg-rev-kpi is-loading" aria-busy="true">
        <div className="tg-rev-kpi-head">
          <span className="tg-rev-kpi-ico" style={{ background: t.bg, color: t.fg }}>
            <Icon size={15} strokeWidth={2.2} />
          </span>
          <span className="tg-rev-kpi-label">{label}</span>
        </div>
        <div className="sk sk-line sk-value" />
        <div className="sk sk-line sk-sub" />
      </div>
    );
  }
  return (
    <div className="tg-rev-kpi">
      <div className="tg-rev-kpi-head">
        <span className="tg-rev-kpi-ico" style={{ background: t.bg, color: t.fg }}>
          <Icon size={15} strokeWidth={2.2} />
        </span>
        <span className="tg-rev-kpi-label">{label}</span>
        {hint ? (
          <AntTooltip title={hint}>
            <span className="tg-rev-kpi-hint">?</span>
          </AntTooltip>
        ) : null}
      </div>
      <div className="tg-rev-kpi-value" style={{ color: t.fg }}>
        {value}
      </div>
      {sub ? <div className="tg-rev-kpi-sub">{sub}</div> : null}
    </div>
  );
};

const Panel = ({ title, subtitle, right, children, className = '' }) => (
  <div className={`tg-rev-panel ${className}`}>
    {(title || right) && (
      <div className="tg-rev-panel-head">
        <div>
          {title ? <h3>{title}</h3> : null}
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        {right}
      </div>
    )}
    <div className="tg-rev-panel-body">{children}</div>
  </div>
);

/* -------------------------------------------------------------------- page */

/** Placeholder slots — same order, labels and icons as the real KPI cards. */
const KPI_SLOTS = [
  { label: 'MRR', icon: Repeat, tone: 'teal' },
  { label: 'Collected (net)', icon: Wallet, tone: 'green' },
  { label: 'Due next 30 days', icon: CalendarClock, tone: 'blue' },
  { label: 'Paying orgs', icon: Users, tone: 'purple' },
  { label: 'Success rate', icon: TrendingUp, tone: 'orange' },
  { label: 'At risk', icon: AlertTriangle, tone: 'red' },
];


/** Fallback labels if the API's vocabulary hasn't arrived yet. */
const REASON_LABELS = {
  too_expensive: 'Too expensive',
  not_using: 'Not using it / no adoption',
  switched_competitor: 'Switched to a competitor',
  missing_features: 'Missing features',
  company_closed_or_downsized: 'Company closed or downsized',
  product_issues: 'Product issues / bugs',
  payment_failed_unintentional: 'Payment failed — not intentional',
  paused_temporarily: 'Paused temporarily',
  other: 'Other',
};

const RANGES = [
  { label: '30D', value: '30d' },
  { label: '90D', value: '90d' },
  { label: '12M', value: '12m' },
  { label: 'YTD', value: 'ytd' },
  { label: 'All', value: 'all' },
  { label: 'Custom', value: 'custom' },
];

const rangeToDates = (range, custom) => {
  const to = dayjs();
  switch (range) {
    case '90d':
      return { from: to.subtract(90, 'day'), to };
    case '12m':
      return { from: to.subtract(12, 'month'), to };
    case 'ytd':
      return { from: dayjs().startOf('year'), to };
    case 'all':
      // Comfortably before TeamGrid took its first payment.
      return { from: dayjs('2023-01-01'), to };
    case 'custom':
      return { from: custom?.[0] || to.subtract(30, 'day'), to: custom?.[1] || to };
    case '30d':
    default:
      return { from: to.subtract(30, 'day'), to };
  }
};

const RevenueDashboard = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const tokens = useMemo(() => getAdminTokens(isDark), [isDark]);
  const designSystem = useMemo(() => getAdminDesignSystem(isDark), [isDark]);

  // 30 days by default: the most-used view and the quickest to build.
  const [range, setRange] = useState('30d');
  const [customRange, setCustomRange] = useState(null);
  // INR is the default view — most of the book is billed in rupees through
  // Razorpay. USD stays one click away via the header toggle.
  const [currency, setCurrency] = useState('INR');
  const [tab, setTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  const [overview, setOverview] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [error, setError] = useState('');

  // Per-tab data + loading, fetched lazily on first activation.
  const [payments, setPayments] = useState({ rows: [], total: 0, totals: {} });
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [payFilters, setPayFilters] = useState({ gateway: null, status: null, q: '', page: 1, pageSize: 50 });

  const [subs, setSubs] = useState({ rows: [], totals: {} });
  const [subsLoading, setSubsLoading] = useState(false);
  const [subFilters, setSubFilters] = useState({ gateway: null, status: null, source: null, q: '' });

  // Attribution scope. The Razorpay merchant account is shared with another
  // product, so 'teamgrid' (the default) is the only trustworthy revenue view;
  // 'all' exists to reconcile against the gateway dashboard, 'foreign' to see
  // exactly what was excluded.
  const [scope, setScope] = useState('teamgrid');
  // Read through a ref so the fetchers don't need `scope` in their dep arrays
  // (which would rebuild every callback and re-trigger the window effect).
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  /**
   * In-flight request control, one slot per resource.
   *
   * Windows build at very different speeds (~5s for 30 days, ~50s for All), so
   * clicking 90D then 30D would otherwise let the slower 90D response land
   * last and silently overwrite the 30D view with the wrong data. Starting a
   * request aborts the previous one for the same resource, and only the
   * newest controller is allowed to write state or clear the loading flag.
   */
  const inflight = useRef({});
  const beginRequest = useCallback((key) => {
    inflight.current[key]?.abort();
    const controller = new AbortController();
    inflight.current[key] = controller;
    return controller;
  }, []);
  const isCurrent = useCallback((key, controller) => inflight.current[key] === controller, []);

  // Drop anything still in flight when the page unmounts.
  useEffect(() => {
    const slots = inflight.current;
    return () => Object.values(slots).forEach((c) => c?.abort());
  }, []);

  const [upcoming, setUpcoming] = useState({ rows: [], totals: {} });
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [upcomingDays, setUpcomingDays] = useState(90);

  const [customers, setCustomers] = useState({ rows: [] });
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');

  const [settlements, setSettlements] = useState({ rows: [], totals: {} });
  const [settlementsLoading, setSettlementsLoading] = useState(false);

  // Retention reads the full 12-month history rather than the range on screen,
  // so it is fetched alongside the overview and not re-fetched on range change.
  const [retention, setRetention] = useState(null);

  const [detail, setDetail] = useState(null); // drawer payload
  // Churn-reason capture: { orgId, orgName, period, mrrLostUsd, existing }
  const [noteFor, setNoteFor] = useState(null);
  const [noteDraft, setNoteDraft] = useState({ reason: undefined, note: '', recoverable: null });
  const [noteSaving, setNoteSaving] = useState(false);

  const dates = useMemo(() => rangeToDates(range, customRange), [range, customRange]);
  const windowParams = useMemo(
    () => ({ from: dates.from.format('YYYY-MM-DD'), to: dates.to.format('YYYY-MM-DD') }),
    [dates]
  );

  // Tabs already loaded once — used so switching back doesn't re-hit the API.
  const loadedTabs = useRef(new Set());

  /* --------------------------------------------------------------- fetchers */

  const fetchOverview = useCallback(
    async (force = false) => {
      const ctl = beginRequest('overview');
      try {
        setLoadingOverview(true);
        setError('');
        const res = await api.get(`${API}/overview`, {
          params: { ...windowParams, ...(force ? { refresh: 1 } : {}) },
          signal: ctl.signal,
        });
        if (!isCurrent('overview', ctl)) return; // superseded by a newer window
        setOverview(res.data.data);
        setMeta(res.data.meta);
      } catch (e) {
        if (wasAborted(e) || !isCurrent('overview', ctl)) return;
        setError(e.response?.data?.message || e.message || 'Failed to load revenue overview');
      } finally {
        if (isCurrent('overview', ctl)) setLoadingOverview(false);
      }
    },
    [api, windowParams, beginRequest, isCurrent]
  );

  const fetchRetention = useCallback(
    async (force = false) => {
      const ctl = beginRequest('retention');
      try {
        const res = await api.get(`${API}/retention`, {
          params: { ...windowParams, months: 12, ...(force ? { refresh: 1 } : {}) },
          signal: ctl.signal,
        });
        if (!isCurrent('retention', ctl)) return;
        setRetention(res.data.data);
      } catch (e) {
        if (wasAborted(e) || !isCurrent('retention', ctl)) return;
        // Non-fatal: the rest of the page is still useful without it.
        setRetention(null);
      }
    },
    [api, windowParams, beginRequest, isCurrent]
  );

  const fetchPayments = useCallback(
    async (force = false, filtersOverride) => {
      const f = filtersOverride || payFilters;
      const ctl = beginRequest('payments');
      try {
        setPaymentsLoading(true);
        const res = await api.get(`${API}/payments`, {
          params: {
            ...windowParams,
            gateway: f.gateway || undefined,
            status: f.status || undefined,
            q: f.q || undefined,
            scope: scopeRef.current,
            page: f.page,
            pageSize: f.pageSize,
            ...(force ? { refresh: 1 } : {}),
          },
          signal: ctl.signal,
        });
        if (!isCurrent('payments', ctl)) return; // superseded by a newer request
        setPayments(res.data.data);
        setMeta(res.data.meta);
      } catch (e) {
        if (wasAborted(e) || !isCurrent('payments', ctl)) return;
        toast.error(e.response?.data?.message || 'Failed to load payments');
      } finally {
        if (isCurrent('payments', ctl)) setPaymentsLoading(false);
      }
    },
    [api, windowParams, payFilters, beginRequest, isCurrent]
  );

  const fetchSubs = useCallback(
    async (force = false, filtersOverride) => {
      const f = filtersOverride || subFilters;
      const ctl = beginRequest('subscriptions');
      try {
        setSubsLoading(true);
        const res = await api.get(`${API}/subscriptions`, {
          params: {
            ...windowParams,
            gateway: f.gateway || undefined,
            status: f.status || undefined,
            source: f.source || undefined,
            q: f.q || undefined,
            scope: scopeRef.current,
            ...(force ? { refresh: 1 } : {}),
          },
          signal: ctl.signal,
        });
        if (!isCurrent('subscriptions', ctl)) return; // superseded by a newer request
        setSubs(res.data.data);
        setMeta(res.data.meta);
      } catch (e) {
        if (wasAborted(e) || !isCurrent('subscriptions', ctl)) return;
        toast.error(e.response?.data?.message || 'Failed to load subscriptions');
      } finally {
        if (isCurrent('subscriptions', ctl)) setSubsLoading(false);
      }
    },
    [api, windowParams, subFilters, beginRequest, isCurrent]
  );

  const fetchUpcoming = useCallback(
    async (force = false, days) => {
      const ctl = beginRequest('upcoming');
      try {
        setUpcomingLoading(true);
        const res = await api.get(`${API}/upcoming`, {
          params: { ...windowParams, days: days || upcomingDays, ...(force ? { refresh: 1 } : {}) },
          signal: ctl.signal,
        });
        if (!isCurrent('upcoming', ctl)) return; // superseded by a newer request
        setUpcoming(res.data.data);
        setMeta(res.data.meta);
      } catch (e) {
        if (wasAborted(e) || !isCurrent('upcoming', ctl)) return;
        toast.error(e.response?.data?.message || 'Failed to load upcoming billing');
      } finally {
        if (isCurrent('upcoming', ctl)) setUpcomingLoading(false);
      }
    },
    [api, windowParams, upcomingDays, beginRequest, isCurrent]
  );

  const fetchCustomers = useCallback(
    async (force = false, q) => {
      const ctl = beginRequest('customers');
      try {
        setCustomersLoading(true);
        const res = await api.get(`${API}/customers`, {
          params: { ...windowParams, q: (q ?? customerQuery) || undefined, ...(force ? { refresh: 1 } : {}) },
          signal: ctl.signal,
        });
        if (!isCurrent('customers', ctl)) return; // superseded by a newer request
        setCustomers(res.data.data);
        setMeta(res.data.meta);
      } catch (e) {
        if (wasAborted(e) || !isCurrent('customers', ctl)) return;
        toast.error(e.response?.data?.message || 'Failed to load customers');
      } finally {
        if (isCurrent('customers', ctl)) setCustomersLoading(false);
      }
    },
    [api, windowParams, customerQuery, beginRequest, isCurrent]
  );

  const fetchSettlements = useCallback(
    async (force = false) => {
      const ctl = beginRequest('settlements');
      try {
        setSettlementsLoading(true);
        const res = await api.get(`${API}/settlements`, {
          params: { ...windowParams, ...(force ? { refresh: 1 } : {}) },
          signal: ctl.signal,
        });
        if (!isCurrent('settlements', ctl)) return; // superseded by a newer request
        setSettlements(res.data.data);
        setMeta(res.data.meta);
      } catch (e) {
        if (wasAborted(e) || !isCurrent('settlements', ctl)) return;
        toast.error(e.response?.data?.message || 'Failed to load settlements');
      } finally {
        if (isCurrent('settlements', ctl)) setSettlementsLoading(false);
      }
    },
    [api, windowParams, beginRequest, isCurrent]
  );

  /* ------------------------------------------------------------- lifecycle */

  // Overview always loads; a window change invalidates every tab.
  useEffect(() => {
    loadedTabs.current = new Set(['overview']);
    fetchOverview(false);
    fetchRetention(false);
    // Re-pull whichever tab is currently open so it matches the new window.
    if (tab !== 'overview') {
      loadedTabs.current.add(tab);
      ({
        payments: () => fetchPayments(false),
        subscriptions: () => fetchSubs(false),
        upcoming: () => fetchUpcoming(false),
        customers: () => fetchCustomers(false),
        settlements: () => fetchSettlements(false),
      }[tab] || (() => {}))();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowParams.from, windowParams.to]);

  const onTabChange = (key) => {
    setTab(key);
    if (loadedTabs.current.has(key)) return;
    loadedTabs.current.add(key);
    ({
      payments: () => fetchPayments(false),
      subscriptions: () => fetchSubs(false),
      upcoming: () => fetchUpcoming(false),
      customers: () => fetchCustomers(false),
      settlements: () => fetchSettlements(false),
    }[key] || (() => {}))();
  };

  /**
   * Hard refresh: busts the server-side ledger cache for this window.
   * Only the FIRST call carries refresh=1 — it rebuilds and re-caches, so the
   * follow-up tab fetch must reuse that fresh build rather than forcing a
   * second round-trip to Stripe and Razorpay.
   */
  const refreshAll = async () => {
    setRefreshing(true);
    loadedTabs.current = new Set(['overview', tab]);
    try {
      await fetchOverview(true);
      fetchRetention(false); // same rebuilt ledger — no second gateway round

      await ({
        payments: () => fetchPayments(false),
        subscriptions: () => fetchSubs(false),
        upcoming: () => fetchUpcoming(false),
        customers: () => fetchCustomers(false),
        settlements: () => fetchSettlements(false),
      }[tab] || (() => Promise.resolve()))();
      toast.success('Revenue data refreshed from Stripe + Razorpay');
    } finally {
      setRefreshing(false);
    }
  };

  const openNote = (row) => {
    setNoteFor(row);
    setNoteDraft({
      reason: row.note?.reason,
      note: row.note?.note || '',
      recoverable: typeof row.note?.recoverable === 'boolean' ? row.note.recoverable : null,
    });
  };

  const saveNote = async () => {
    if (!noteFor || !noteDraft.reason) {
      toast.error('Pick a reason first');
      return;
    }
    try {
      setNoteSaving(true);
      await api.post(`${API}/churn-notes`, {
        orgId: noteFor.orgId,
        orgName: noteFor.orgName,
        period: noteFor.period || '',
        reason: noteDraft.reason,
        note: noteDraft.note,
        recoverable: noteDraft.recoverable,
        mrrAtChurnUsd: noteFor.mrrLostUsd ?? noteFor.mrrUsd ?? null,
      });
      toast.success('Reason saved');
      setNoteFor(null);
      fetchRetention(false); // re-read so the row shows it immediately
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the reason');
    } finally {
      setNoteSaving(false);
    }
  };

  const deleteNote = async () => {
    if (!noteFor?.note?._id) return;
    try {
      setNoteSaving(true);
      await api.delete(`${API}/churn-notes/${noteFor.note._id}`);
      toast.success('Reason removed');
      setNoteFor(null);
      fetchRetention(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not remove the reason');
    } finally {
      setNoteSaving(false);
    }
  };

  const exportCsv = async (explicitType) => {
    const type =
      typeof explicitType === 'string'
        ? explicitType
        : ['payments', 'subscriptions', 'customers', 'upcoming', 'settlements'].includes(tab)
        ? tab
        : 'payments';
    try {
      const res = await api.get(`${API}/export`, {
        params: { ...windowParams, type },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue-${type}-${dayjs().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${type}.csv`);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  /* ---------------------------------------------------------------- charts */

  const chartBase = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        background: 'transparent',
        fontFamily: 'Inter, sans-serif',
        animations: { enabled: true, easing: 'easeinout', speed: 500 },
      },
      theme: { mode: isDark ? 'dark' : 'light' },
      grid: { borderColor: tokens.hair, strokeDashArray: 4, padding: { left: 4, right: 4 } },
      dataLabels: { enabled: false },
      // `fillSeriesColor` (ApexCharts' default) paints the tooltip background
      // with the series colour, which left dark label text on a saturated
      // purple/blue chip and was unreadable. Force a panel-coloured surface
      // with our own text colour instead, in both themes.
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        fillSeriesColor: false,
        style: { fontSize: '12px', fontFamily: 'Inter, sans-serif' },
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        markers: { radius: 4 },
        labels: { colors: tokens.text2 },
      },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: tokens.text3, fontSize: '11px' } },
      },
      yaxis: {
        labels: {
          style: { colors: tokens.text3, fontSize: '11px' },
          formatter: (v) => money(v, currency, { compact: true, decimals: 0 }),
        },
      },
    }),
    [isDark, tokens, currency]
  );

  const series = useMemo(() => overview?.series || [], [overview]);
  const trendOptions = useMemo(
    () => ({
      ...chartBase,
      chart: { ...chartBase.chart, type: 'bar', stacked: true },
      colors: ['#635bff', '#3395ff'],
      plotOptions: { bar: { borderRadius: 6, columnWidth: series.length > 14 ? '75%' : '45%' } },
      xaxis: {
        ...chartBase.xaxis,
        categories: series.map((s) => dayjs(`${s.period}${s.period.length === 7 ? '-01' : ''}`).format(s.period.length === 7 ? 'MMM YY' : 'DD MMM')),
      },
      tooltip: {
        ...chartBase.tooltip,
        y: { formatter: (v) => money(v, currency) },
      },
    }),
    [chartBase, series, currency]
  );
  const trendSeries = useMemo(
    () => [
      { name: 'Stripe', data: series.map((s) => Number((currency === 'INR' ? s.stripeInr : s.stripeUsd) || 0).toFixed(2)) },
      { name: 'Razorpay', data: series.map((s) => Number((currency === 'INR' ? s.razorpayInr : s.razorpayUsd) || 0).toFixed(2)) },
    ],
    [series, currency]
  );

  const mrr = overview?.mrr || {};
  const rate = meta?.fx?.usdInr || 87;
  const inCur = (usdValue) => (currency === 'INR' ? (usdValue || 0) * rate : usdValue || 0);

  const mrrDonutOptions = useMemo(
    () => ({
      chart: { type: 'donut', background: 'transparent', fontFamily: 'Inter, sans-serif' },
      theme: { mode: isDark ? 'dark' : 'light' },
      labels: ['Stripe recurring', 'Razorpay recurring', 'Manual / one-time'],
      colors: ['#635bff', '#3395ff', '#94a3b8'],
      legend: { position: 'bottom', fontSize: '12px', labels: { colors: tokens.text2 }, markers: { radius: 4 } },
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        fillSeriesColor: false,
        style: { fontSize: '12px', fontFamily: 'Inter, sans-serif' },
        y: { formatter: (v) => money(v, currency) },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '72%',
            labels: {
              show: true,
              name: { fontSize: '12px', color: tokens.text3 },
              value: {
                fontSize: '20px',
                fontWeight: 700,
                color: tokens.text,
                formatter: (v) => money(v, currency, { compact: true }),
              },
              total: {
                show: true,
                label: 'Total MRR',
                fontSize: '12px',
                color: tokens.text3,
                formatter: () => money(inCur(mrr.total), currency, { compact: true }),
              },
            },
          },
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDark, tokens, currency, mrr.total, rate]
  );
  const mrrDonutSeries = useMemo(
    () => [inCur(mrr.stripe), inCur(mrr.razorpay), inCur(mrr.manual)].map((v) => Number((v || 0).toFixed(2))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mrr.stripe, mrr.razorpay, mrr.manual, currency, rate]
  );

  /* --------------------------------------------------------- retention viz */
  const ret = retention || overview?.retention || null;
  const retSeries = useMemo(() => ret?.series || [], [ret]);

  const retentionOptions = useMemo(
    () => ({
      ...chartBase,
      chart: { ...chartBase.chart, type: 'line', stacked: false },
      colors: ['#1aa363', '#e5514b', tokens.accent],
      stroke: { width: [0, 0, 2.5], curve: 'smooth' },
      plotOptions: { bar: { borderRadius: 5, columnWidth: '55%' } },
      markers: { size: 3, strokeWidth: 0 },
      xaxis: {
        ...chartBase.xaxis,
        categories: retSeries.map((m) => dayjs(`${m.period}-01`).format('MMM YY')),
      },
      yaxis: [
        {
          seriesName: 'New',
          labels: { style: { colors: tokens.text3, fontSize: '11px' }, formatter: (v) => Math.round(v) },
          title: { text: 'customers', style: { color: tokens.text3, fontSize: '11px', fontWeight: 500 } },
        },
        { seriesName: 'New', show: false },
        {
          opposite: true,
          min: 0,
          max: 100,
          labels: { style: { colors: tokens.text3, fontSize: '11px' }, formatter: (v) => `${Math.round(v)}%` },
          title: { text: 'retained', style: { color: tokens.text3, fontSize: '11px', fontWeight: 500 } },
        },
      ],
      tooltip: {
        ...chartBase.tooltip,
        shared: true,
        intersect: false,
        y: { formatter: (v, o) => (o?.seriesIndex === 2 ? `${Number(v).toFixed(1)}%` : Math.round(v)) },
      },
    }),
    [chartBase, retSeries, tokens]
  );
  const retentionChartSeries = useMemo(
    () => [
      { name: 'New', type: 'column', data: retSeries.map((m) => m.newCount) },
      { name: 'Churned', type: 'column', data: retSeries.map((m) => -m.churnedCount) },
      { name: 'Retained %', type: 'line', data: retSeries.map((m) => Number((m.logoRetentionPct || 0).toFixed(1))) },
    ],
    [retSeries]
  );

  const atRisk = retention?.atRisk || null;

  const planMix = useMemo(() => {
    const raw = overview?.planMix || {};
    return Object.entries(raw)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.mrrUsd - a.mrrUsd)
      .slice(0, 6);
  }, [overview]);

  /* ---------------------------------------------------------------- tables */

  // Stable identity so the memoised column definitions below don't capture a
  // stale closure (and don't have to rebuild on every render).
  const orgCell = useCallback((name, orgId) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 600, fontSize: 13.5, color: tokens.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {name || <span style={{ color: tokens.text3, fontWeight: 500 }}>Unattributed</span>}
      </div>
      {orgId ? <div style={{ fontSize: 11, color: tokens.text3, fontFamily: 'ui-monospace, monospace' }}>{orgId}</div> : null}
    </div>
  ), [tokens]);

  const paymentColumns = useMemo(() => [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      width: 150,
      render: (v) => <span style={{ fontSize: 13 }}>{dateTimeText(v)}</span>,
    },
    {
      title: 'Organization',
      dataIndex: 'orgName',
      width: 230,
      render: (v, r) => orgCell(v, r.orgId),
    },
    {
      title: 'Gateway',
      dataIndex: 'gateway',
      width: 110,
      render: (v) => <GatewayTag gateway={v} />,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 150,
      align: 'right',
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13.5 }}>{money(v, r.currency)}</div>
          <div style={{ fontSize: 11, color: tokens.text3 }}>≈ {money(pick(r, 'net', currency), currency)}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (v) => <Pill status={v} />,
    },
    {
      title: 'Method',
      dataIndex: 'method',
      width: 130,
      render: (v) => <span style={{ fontSize: 12.5, color: tokens.text2, textTransform: 'capitalize' }}>{v || '—'}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      ellipsis: true,
      render: (v, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, color: tokens.text2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</div>
          <div style={{ fontSize: 11, color: tokens.text3, fontFamily: 'ui-monospace, monospace' }}>{r.id}</div>
        </div>
      ),
    },
    {
      title: '',
      width: 56,
      align: 'right',
      render: (_, r) =>
        r.dashboardUrl ? (
          <AntTooltip title={`Open in ${r.gateway === 'razorpay' ? 'Razorpay' : 'Stripe'} dashboard`}>
            <a href={r.dashboardUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
              <ExternalLink size={15} style={{ color: tokens.text3 }} />
            </a>
          </AntTooltip>
        ) : null,
    },
  ], [currency, tokens, orgCell]);

  const subscriptionColumns = useMemo(() => [
    { title: 'Organization', dataIndex: 'orgName', width: 230, render: (v, r) => orgCell(v, r.orgId) },
    {
      title: 'Source',
      dataIndex: 'gateway',
      width: 130,
      render: (v, r) => (
        <div>
          <GatewayTag gateway={v} source={r.source} />
          {r.orphaned ? (
            <AntTooltip title="This subscription is live at the gateway but its organization no longer exists in TeamGrid — it is still charging a card.">
              <div style={{ fontSize: 11, color: '#e5514b', fontWeight: 600, marginTop: 2 }}>orphaned</div>
            </AntTooltip>
          ) : null}
        </div>
      ),
    },
    { title: 'Plan', dataIndex: 'planName', width: 140, render: (v) => <span style={{ fontSize: 13 }}>{v || '—'}</span> },
    { title: 'Seats', dataIndex: 'seats', width: 80, align: 'right', render: (v) => <span style={{ fontSize: 13 }}>{v || 0}</span> },
    {
      title: 'Per cycle',
      dataIndex: 'amount',
      width: 140,
      align: 'right',
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>{money(v, r.currency)}</div>
          <div style={{ fontSize: 11, color: tokens.text3 }}>/{r.interval === 'year' || r.interval === 'yearly' ? 'yr' : 'mo'}</div>
        </div>
      ),
    },
    {
      title: 'MRR',
      dataIndex: 'mrrUsd',
      width: 120,
      align: 'right',
      sorter: (a, b) => (a.mrrUsd || 0) - (b.mrrUsd || 0),
      render: (_, r) => (
        <span style={{ fontWeight: 700, fontSize: 13.5, color: r.countsTowardMrr ? tokens.okTx : tokens.text3 }}>
          {money(pick(r, 'mrr', currency), currency)}
        </span>
      ),
    },
    { title: 'Status', dataIndex: 'status', width: 120, render: (v) => <Pill status={v} /> },
    {
      title: 'Auto-pay',
      dataIndex: 'autoPay',
      width: 110,
      render: (v, r) =>
        r.source === 'manual' ? (
          <AntTooltip title="No gateway mandate — this org must be invoiced manually every cycle.">
            <span style={{ fontSize: 12.5, color: tokens.warnTx, fontWeight: 600 }}>Manual</span>
          </AntTooltip>
        ) : (
          <span style={{ fontSize: 12.5, color: v ? tokens.okTx : tokens.text3, fontWeight: 600 }}>{v ? 'On' : 'Off'}</span>
        ),
    },
    {
      title: 'Next charge',
      dataIndex: 'nextChargeAt',
      width: 150,
      render: (v, r) => (
        <div>
          <div style={{ fontSize: 13 }}>{dateText(v || r.currentPeriodEnd)}</div>
          {r.overdue ? <div style={{ fontSize: 11, color: '#e5514b', fontWeight: 600 }}>overdue</div> : null}
        </div>
      ),
    },
  ], [currency, tokens, orgCell]);

  const upcomingColumns = useMemo(() => [
    {
      title: 'Due',
      dataIndex: 'dueAt',
      width: 160,
      render: (v, r) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{dateText(v)}</div>
          <div style={{ fontSize: 11, color: r.overdue ? '#e5514b' : tokens.text3, fontWeight: r.overdue ? 600 : 400 }}>
            {r.overdue ? `${Math.abs(r.daysAway)}d overdue` : r.daysAway === 0 ? 'today' : `in ${r.daysAway}d`}
          </div>
        </div>
      ),
    },
    { title: 'Organization', dataIndex: 'orgName', width: 230, render: (v, r) => orgCell(v, r.orgId) },
    { title: 'Source', dataIndex: 'gateway', width: 120, render: (v, r) => <GatewayTag gateway={v} source={r.source} /> },
    { title: 'Plan', dataIndex: 'plan', width: 130, render: (v) => <span style={{ fontSize: 13 }}>{v || '—'}</span> },
    { title: 'Seats', dataIndex: 'seats', width: 75, align: 'right' },
    {
      title: 'Expected',
      dataIndex: 'expectedUsd',
      width: 150,
      align: 'right',
      sorter: (a, b) => (a.expectedUsd || 0) - (b.expectedUsd || 0),
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13.5 }}>{money(r.amount, r.currency)}</div>
          <div style={{ fontSize: 11, color: tokens.text3 }}>≈ {money(pick(r, 'expected', currency), currency)}</div>
        </div>
      ),
    },
    {
      title: 'Collection',
      dataIndex: 'willAutoCollect',
      width: 150,
      render: (v) =>
        v ? (
          <span style={{ fontSize: 12.5, color: tokens.okTx, fontWeight: 600 }}>Auto-charges</span>
        ) : (
          <AntTooltip title="No armed mandate behind this renewal — it will not collect itself. Someone has to chase it.">
            <span style={{ fontSize: 12.5, color: tokens.warnTx, fontWeight: 600 }}>Needs chasing</span>
          </AntTooltip>
        ),
    },
    { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Pill status={v} /> },
  ], [currency, tokens, orgCell]);

  const customerColumns = useMemo(() => [
    { title: 'Organization', dataIndex: 'orgName', width: 260, render: (v, r) => orgCell(v, r.orgId) },
    {
      title: 'Gateways',
      dataIndex: 'gateways',
      width: 150,
      render: (v = []) => (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {v.length ? v.map((g) => <GatewayTag key={g} gateway={g} />) : <span style={{ color: tokens.text3 }}>—</span>}
        </div>
      ),
    },
    { title: 'Plan', dataIndex: 'plan', width: 120, render: (v) => <span style={{ fontSize: 13 }}>{v || '—'}</span> },
    { title: 'Status', dataIndex: 'status', width: 115, render: (v) => <Pill status={v} /> },
    {
      title: 'MRR',
      dataIndex: 'mrrUsd',
      width: 120,
      align: 'right',
      sorter: (a, b) => (a.mrrUsd || 0) - (b.mrrUsd || 0),
      render: (_, r) => <span style={{ fontWeight: 600, fontSize: 13.5 }}>{money(pick(r, 'mrr', currency), currency)}</span>,
    },
    {
      title: 'Lifetime',
      dataIndex: 'ltvUsd',
      width: 130,
      align: 'right',
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.ltvUsd || 0) - (b.ltvUsd || 0),
      render: (_, r) => <span style={{ fontWeight: 700, fontSize: 13.5, color: tokens.okTx }}>{money(pick(r, 'ltv', currency), currency)}</span>,
    },
    { title: 'Payments', dataIndex: 'payments', width: 100, align: 'right', render: (v, r) => (
      <div>
        <div style={{ fontSize: 13 }}>{v || 0}</div>
        {r.failedPayments ? <div style={{ fontSize: 11, color: '#e5514b' }}>{r.failedPayments} failed</div> : null}
      </div>
    ) },
    { title: 'Last paid', dataIndex: 'lastPaymentAt', width: 130, render: (v) => <span style={{ fontSize: 13 }}>{dateText(v)}</span> },
    { title: 'Next charge', dataIndex: 'nextChargeAt', width: 130, render: (v) => <span style={{ fontSize: 13 }}>{dateText(v)}</span> },
  ], [currency, tokens, orgCell]);

  const churnedColumns = useMemo(() => [
    { title: 'Left in', dataIndex: 'period', width: 110,
      render: (v) => <span style={{ fontSize: 13, fontWeight: 600 }}>{dayjs(`${v}-01`).format('MMM YYYY')}</span> },
    { title: 'Organization', dataIndex: 'orgName', width: 240, render: (v, r) => orgCell(v, r.orgId) },
    {
      title: 'MRR lost', dataIndex: 'mrrLostUsd', width: 130, align: 'right',
      defaultSortOrder: 'descend', sorter: (a, b) => a.mrrLostUsd - b.mrrLostUsd,
      render: (_, r) => <span style={{ fontWeight: 700, fontSize: 13.5, color: '#e5514b' }}>{money(pick(r, 'mrrLost', currency), currency)}</span>,
    },
    { title: 'Last paid', dataIndex: 'lastPaidAt', width: 120,
      render: (v) => <span style={{ fontSize: 13 }}>{v ? dayjs(`${v}-01`).format('MMM YYYY') : '—'}</span> },
    {
      title: 'Why they left',
      dataIndex: 'note',
      render: (v) =>
        v ? (
          <div style={{ minWidth: 0 }}>
            <span
              style={{
                fontSize: 11.5, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
                background: isDark ? 'rgba(107,78,240,.16)' : '#efeafe', color: isDark ? '#a892f8' : '#6b4ef0',
              }}
            >
              {REASON_LABELS[v.reason] || v.reason}
            </span>
            {v.note ? (
              <div style={{ fontSize: 12.5, color: tokens.text2, marginTop: 4, lineHeight: 1.45 }}>{v.note}</div>
            ) : null}
            {v.updatedBy?.email || v.createdBy?.email ? (
              <div style={{ fontSize: 10.5, color: tokens.text3, marginTop: 2 }}>
                — {v.updatedBy?.name || v.updatedBy?.email || v.createdBy?.name || v.createdBy?.email}
              </div>
            ) : null}
          </div>
        ) : (
          <span style={{ fontSize: 12.5, color: tokens.text3, fontStyle: 'italic' }}>no reason recorded</span>
        ),
    },
    {
      title: '', width: 120, align: 'right',
      render: (_, r) => (
        <AntButton size="small" type={r.note ? 'default' : 'primary'} onClick={() => openNote(r)}>
          {r.note ? 'Edit' : 'Add reason'}
        </AntButton>
      ),
    },
  ], [currency, tokens, orgCell, isDark]);

  const atRiskColumns = useMemo(() => [
    {
      title: 'Organization',
      dataIndex: 'orgName',
      width: 230,
      render: (v, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <span
            title={r.severity}
            style={{
              width: 8, height: 8, borderRadius: 999, flex: 'none',
              background: r.severity === 'critical' ? '#e5514b' : r.severity === 'warning' ? '#e0a020' : tokens.text3,
            }}
          />
          {orgCell(v, r.orgId)}
        </div>
      ),
    },
    {
      title: 'Adoption',
      dataIndex: 'adoptionPct',
      width: 190,
      sorter: (a, b) => a.adoptionPct - b.adoptionPct,
      render: (v, r) => (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: tokens.text2, marginBottom: 4 }}>
            <span>
              <b style={{ color: r.activeUsers ? tokens.text : '#e5514b' }}>{r.activeUsers}</b> of {r.seats} seats
            </span>
            <span style={{ color: tokens.text3 }}>{Math.round(v)}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 999, background: tokens.hair, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${Math.max(2, v)}%`,
                borderRadius: 999,
                background: v === 0 ? '#e5514b' : v < 25 ? '#e0a020' : tokens.accent,
              }}
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Tracked by',
      dataIndex: 'appUsers',
      width: 150,
      render: (_, r) => (
        <span style={{ fontSize: 12.5, color: tokens.text2 }}>
          {r.appUsers || r.agentUsers
            ? [r.appUsers ? `${r.appUsers} app` : null, r.agentUsers ? `${r.agentUsers} agent` : null].filter(Boolean).join(' · ')
            : <span style={{ color: '#e5514b' }}>nobody</span>}
        </span>
      ),
    },
    {
      title: 'MRR at risk',
      dataIndex: 'mrrUsd',
      width: 130,
      align: 'right',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.mrrUsd - b.mrrUsd,
      render: (_, r) => (
        <span style={{ fontWeight: 700, fontSize: 13.5, color: '#e5514b' }}>{money(pick(r, 'mrr', currency), currency)}</span>
      ),
    },
    {
      title: 'Why',
      dataIndex: 'reasons',
      render: (v = []) => (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {v.map((x) => (
            <span
              key={x}
              style={{
                fontSize: 11.5, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
                background: isDark ? 'rgba(229,81,75,.14)' : '#fdecea', color: '#e5514b', whiteSpace: 'nowrap',
              }}
            >
              {x}
            </span>
          ))}
        </div>
      ),
    },
    { title: 'Next charge', dataIndex: 'nextChargeAt', width: 130, render: (v) => <span style={{ fontSize: 13 }}>{dateText(v)}</span> },
  ], [currency, tokens, orgCell, isDark]);

  const settlementColumns = useMemo(() => [
    { title: 'Settled on', dataIndex: 'createdAt', width: 170, render: (v) => <span style={{ fontSize: 13 }}>{dateTimeText(v)}</span> },
    { title: 'Settlement ID', dataIndex: 'id', width: 200, render: (v) => <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>{v}</span> },
    { title: 'UTR', dataIndex: 'utr', width: 180, render: (v) => <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>{v || '—'}</span> },
    { title: 'Amount', dataIndex: 'amount', align: 'right', width: 140, render: (v) => <span style={{ fontWeight: 700 }}>{money(v, 'INR')}</span> },
    { title: 'Fees', dataIndex: 'fees', align: 'right', width: 120, render: (v) => <span style={{ color: tokens.text2 }}>{money(v, 'INR')}</span> },
    { title: 'Tax', dataIndex: 'tax', align: 'right', width: 110, render: (v) => <span style={{ color: tokens.text2 }}>{money(v, 'INR')}</span> },
    { title: 'Status', dataIndex: 'status', width: 130, render: (v) => <Pill status={v === 'processed' ? 'succeeded' : v} /> },
  ], [tokens]);

  /* ------------------------------------------------------------------- KPIs */

  const collected = overview?.collected || {};
  const counts = overview?.counts || {};
  const risk = overview?.risk || {};
  const upcomingTotals = overview?.upcomingTotals || {};
  const excluded = overview?.excluded || null;

  const kpis = [
    {
      icon: Repeat,
      label: 'MRR',
      tone: 'teal',
      value: money(inCur(mrr.total), currency, { compact: true }),
      sub: `ARR ${money(inCur(mrr.arrUsd), currency, { compact: true })} · ${counts.liveSubscriptions || 0} paying subs${
        mrr.trialing ? ` · ${money(inCur(mrr.trialing), currency, { compact: true })} in trials` : ''
      }`,
      hint:
        'Monthly recurring value of active and past-due subscriptions — gateway-backed and manual combined. ' +
        'Trials are excluded (nobody has paid for one yet) and reported separately as pipeline.',
    },
    {
      icon: Wallet,
      label: 'Collected (net)',
      tone: 'green',
      value: money(inCur(collected.netUsd), currency, { compact: true }),
      sub: `${collected.count || 0} payments · ${money(inCur(collected.refundUsd), currency, { compact: true })} refunded`,
      hint: 'Gross successful payments in the selected window, minus refunds.',
    },
    {
      icon: CalendarClock,
      label: 'Due next 30 days',
      tone: 'blue',
      value: money(inCur(upcomingTotals.next30Usd), currency, { compact: true }),
      sub: `${upcomingTotals.next30Count || 0} renewals · ${money(inCur(upcomingTotals.overdueUsd), currency, { compact: true })} overdue`,
      hint: 'Expected renewal value based on each subscription’s current price and seat count.',
    },
    {
      icon: Users,
      label: 'Paying orgs',
      tone: 'purple',
      value: (counts.payingOrgs || 0).toLocaleString(),
      sub: `${counts.seats || 0} seats · ARPA ${money(inCur(mrr.arpaUsd), currency)}`,
      hint: 'Organizations contributing to MRR right now.',
    },
    {
      icon: TrendingUp,
      label: 'Success rate',
      tone: 'orange',
      value: `${(collected.successRate || 0).toFixed(1)}%`,
      sub: `${collected.failedCount || 0} failed · avg ${money(inCur(collected.avgTicketUsd), currency)}`,
      hint: 'Share of payment attempts in the window that succeeded.',
    },
    {
      icon: AlertTriangle,
      label: 'At risk',
      tone: 'red',
      value: (
        (risk.pastDue || 0) +
        (risk.overdueManual || 0) +
        (risk.orphanedSubs || 0)
      ).toLocaleString(),
      sub: `${risk.pastDue || 0} past due · ${risk.overdueManual || 0} lapsed · ${risk.orphanedSubs || 0} orphaned`,
      hint:
        'Past-due subscriptions, manually-billed orgs past their period end, and live subscriptions still charging for a deleted org. ' +
        'Cancelled or never-authenticated subscriptions are not counted.',
    },
  ];

  /* ------------------------------------------------------------------ style */

  const pageCss = `
    /* ---- header ------------------------------------------------------
       Title left, controls right, on ONE line. The controls used to sit in a
       wrapping flex box, so at anything under a very wide viewport the
       range/currency/action groups broke onto separate rows and read as a
       vertical stack. They now stay in a single row and scroll sideways
       instead of wrapping, and every control is forced to the same height so
       the pills line up. */
    .tg-rev-head {
      display:flex; align-items:center; justify-content:space-between;
      gap:16px; flex-wrap:wrap;
    }
    .tg-rev-head-title { min-width:240px; flex:1 1 auto; }
    .tg-rev-controls {
      display:flex; align-items:center; gap:10px;
      flex-wrap:nowrap; flex:0 0 auto;
      max-width:100%; overflow-x:auto; overflow-y:hidden;
      scrollbar-width:none; padding:2px 0;
    }
    .tg-rev-controls::-webkit-scrollbar { display:none; }
    .tg-rev-controls > * { flex:0 0 auto; }
    .tg-rev-controls .ant-segmented,
    .tg-rev-controls .ant-btn,
    .tg-rev-controls .ant-picker {
      height:40px !important; display:inline-flex; align-items:center;
    }
    .tg-rev-controls .ant-btn > span { line-height:1; }
    @media (max-width: 1180px) {
      .tg-rev-controls { width:100%; flex:1 1 100%; justify-content:flex-start; }
    }

    /* ---- excluded-revenue note ---------------------------------------
       Text and scope toggle side by side. A margin-left:auto on a wrapping
       flex row pushed the toggle onto its own line whenever the sentence
       filled the width, which it always does. */
    .tg-rev-note {
      display:flex; align-items:center; gap:12px; flex-wrap:nowrap;
      font-size:12.5px; color:${tokens.text2}; background:${tokens.panel2};
      border:1px solid ${tokens.border}; border-radius:14px; padding:10px 14px;
    }
    .tg-rev-note-text { flex:1 1 auto; min-width:0; line-height:1.5; }
    .tg-rev-note .ant-segmented { flex:0 0 auto; }
    @media (max-width: 900px) {
      .tg-rev-note { flex-wrap:wrap; align-items:flex-start; }
      .tg-rev-note-text { flex:1 1 100%; }
    }

    /* ---- horizontal containment ---------------------------------------
       The page body is a CSS grid, and grid/flex items default to
       min-width:auto — they refuse to shrink below their content's
       min-content width. Each tab holds a table with a different
       scroll.x (1000–1300px), so the grid was forcing the whole page wider
       by a DIFFERENT amount per tab: clicking a tab visibly stretched the
       layout sideways. Measured: 944px of space, 1358px of scroll width.
       min-width:0 down the chain lets the tables scroll inside themselves
       instead of pushing the page out. */
    .tg-rev-page { display:grid; gap:18px; min-width:0; }
    .tg-rev-page > * { min-width:0; }
    .tg-rev-grid-2 > *, .tg-rev-grid-3 > * { min-width:0; }
    .tg-rev-panel, .tg-rev-panel-body { min-width:0; }
    .tg-rev-table { min-width:0; max-width:100%; }
    .tg-admin-root .ant-tabs,
    .tg-admin-root .ant-tabs-content-holder,
    .tg-admin-root .ant-tabs-content,
    .tg-admin-root .ant-tabs-tabpane { min-width:0; }
    /* Belt and braces: the page must never scroll sideways as a whole. */
    .tg-admin-root .tg-rev-scroll { overflow-x:hidden; }

    /* Tabs have very different heights, so the vertical scrollbar appears and
       disappears as you switch. That changes the content width by the
       scrollbar's size and reflows the KPI grid — which looks exactly like the
       cards jumping left-to-right. Reserving the gutter keeps the width fixed. */
    .tg-admin-root .tg-rev-scroll { scrollbar-gutter: stable; }

    /* ---- Segmented controls -------------------------------------------
       AntD's defaults left the selected pill flush against the track edge,
       dropped a tinted glow beneath it, and sized the header and in-banner
       toggles differently. One treatment, applied to both. */
    .tg-admin-root .tg-rev-seg.ant-segmented {
      background: ${tokens.hair};
      border: 1px solid ${tokens.border2};
      border-radius: 12px;
      padding: 4px;
      box-shadow: none;
      box-sizing: border-box;
    }
    .tg-admin-root .tg-rev-seg .ant-segmented-group { gap: 2px; }
    .tg-admin-root .tg-rev-seg .ant-segmented-item {
      border-radius: 8px;
      color: ${tokens.text2};
      transition: color .18s ease, background .18s ease;
    }
    .tg-admin-root .tg-rev-seg .ant-segmented-item::after { display: none; }
    .tg-admin-root .tg-rev-seg .ant-segmented-item:hover:not(.ant-segmented-item-selected) {
      background: ${tokens.panel};
      color: ${tokens.text};
    }
    .tg-admin-root .tg-rev-seg .ant-segmented-item-selected,
    .tg-admin-root .tg-rev-seg .ant-segmented-thumb {
      background: ${tokens.accent};
      border-radius: 8px;
      /* neutral lift, not a coloured halo */
      box-shadow: 0 1px 2px rgba(16, 24, 40, 0.14);
    }
    .tg-admin-root .tg-rev-seg .ant-segmented-item-selected .ant-segmented-item-label {
      color: #fff; font-weight: 600;
    }
    /* Only the active choice is emphasised; the rest stay quiet. */
    .tg-admin-root .tg-rev-seg .ant-segmented-item-label {
      display: flex; align-items: center; justify-content: center;
      white-space: nowrap; line-height: 1; font-weight: 500;
      letter-spacing: -0.005em;
    }
    /* header size */
    .tg-admin-root .tg-rev-controls .tg-rev-seg .ant-segmented-item-label {
      height: 30px; padding-inline: 15px; font-size: 13.5px;
    }
    /* in-banner size */
    .tg-admin-root .tg-rev-note .tg-rev-seg .ant-segmented-item-label {
      height: 26px; padding-inline: 14px; font-size: 12.5px;
    }

    .tg-rev-kpis { display:grid; grid-template-columns:repeat(auto-fit,minmax(190px,1fr)); gap:12px; }

    .tg-rev-kpi {
      background:${tokens.panel}; border:1px solid ${tokens.border}; border-radius:18px; padding:16px 18px;
      box-shadow:${tokens.sh}; transition:transform .18s ease, box-shadow .18s ease; animation:tgRise .4s ease both;
    }
    .tg-rev-kpi:hover { transform:translateY(-2px); box-shadow:${tokens.shLift}; }
    .tg-rev-kpi-head { display:flex; align-items:center; gap:8px; margin-bottom:10px; }
    .tg-rev-kpi-ico { width:28px; height:28px; border-radius:9px; display:inline-flex; align-items:center; justify-content:center; }
    .tg-rev-kpi-label { font-size:12px; font-weight:600; color:${tokens.text3}; letter-spacing:.01em; }
    .tg-rev-kpi-hint {
      margin-left:auto; width:16px; height:16px; border-radius:999px; font-size:10px; font-weight:700; cursor:help;
      display:inline-flex; align-items:center; justify-content:center; color:${tokens.text3}; border:1px solid ${tokens.border2};
    }
    .tg-rev-kpi-value { font-size:26px; font-weight:800; letter-spacing:-.03em; line-height:1.1; }
    .tg-rev-kpi-sub { font-size:11.5px; color:${tokens.text3}; margin-top:6px; }

    .tg-rev-panel {
      background:${tokens.panel}; border:1px solid ${tokens.border}; border-radius:20px;
      box-shadow:${tokens.sh}; overflow:hidden; animation:tgFade .4s ease both;
    }
    .tg-rev-panel-head {
      display:flex; align-items:flex-start; justify-content:space-between; gap:12px;
      padding:16px 20px 12px; border-bottom:1px solid ${tokens.hair};
    }
    .tg-rev-panel-head h3 { font-size:14.5px; font-weight:700; color:${tokens.text}; letter-spacing:-.015em; margin:0; }
    .tg-rev-panel-head p { font-size:12px; color:${tokens.text3}; margin:3px 0 0; }
    .tg-rev-panel-body { padding:16px 20px 18px; }
    .tg-rev-panel-body.flush { padding:0; }

    .tg-rev-risk { display:flex; align-items:center; justify-content:space-between; padding:11px 0; border-bottom:1px dashed ${tokens.hair}; }
    .tg-rev-risk:last-child { border-bottom:none; }
    .tg-rev-risk-l { display:flex; align-items:center; gap:10px; font-size:13px; color:${tokens.text2}; }
    .tg-rev-risk-v { font-size:15px; font-weight:700; }

    /* Fixed-height rows so nothing appears out of nowhere and shoves the KPI
       strip down when the first response lands. */
    .tg-rev-srcrow { display:flex; gap:10px; flex-wrap:wrap; align-items:center; min-height:30px; }
    .tg-rev-src {
      display:flex; align-items:center; gap:8px; font-size:12px; padding:6px 11px;
      border-radius:999px; border:1px solid ${tokens.border}; background:${tokens.panel2};
      min-height:30px; box-sizing:border-box;
    }
    .tg-rev-src.sk-pill { width:210px; border-style:dashed; opacity:.55; }

    /* One slot shared by the loading notice and the excluded-revenue banner:
       same height either way, so swapping them moves nothing. */
    .tg-rev-note { min-height:58px; box-sizing:border-box; }

    /* ---- placeholders --------------------------------------------------
       Shaped like the real KPI content so the card box is byte-identical
       before and after load. */
    @keyframes tgShimmer { 0% { background-position:-320px 0; } 100% { background-position:320px 0; } }
    .tg-rev-kpi { min-height:122px; box-sizing:border-box; }
    .tg-rev-kpi .sk {
      display:block; border-radius:7px;
      background:linear-gradient(90deg, ${tokens.hair} 25%, ${tokens.border} 37%, ${tokens.hair} 63%);
      background-size:640px 100%;
      animation:tgShimmer 1.3s linear infinite;
    }
    .tg-rev-kpi .tg-rev-kpi-ico.sk { border-radius:9px; }
    .tg-rev-kpi .sk-line { height:11px; }
    .tg-rev-kpi .sk-value { height:26px; width:64%; border-radius:8px; }
    .tg-rev-kpi .sk-sub { height:10px; width:82%; margin-top:10px; }
    .tg-rev-kpi.is-loading { animation:none; }

    .tg-rev-plan { display:flex; align-items:center; gap:12px; padding:9px 0; }
    .tg-rev-plan-name { font-size:13px; font-weight:600; color:${tokens.text}; width:96px; flex:none; }
    .tg-rev-plan-bar { flex:1; height:8px; border-radius:999px; background:${tokens.hair}; overflow:hidden; }
    .tg-rev-plan-fill { height:100%; border-radius:999px; background:linear-gradient(90deg, ${tokens.accent}, ${tokens.accent2}); }
    .tg-rev-plan-val { font-size:12.5px; font-weight:700; color:${tokens.text}; width:88px; text-align:right; flex:none; }

    .tg-admin-root .ant-tabs-nav { margin-bottom:16px; }
    .tg-admin-root .ant-tabs-tab { font-size:13.5px; font-weight:600; }
    .tg-admin-root .tg-rev-table .ant-table { background:transparent; }
    .tg-admin-root .tg-rev-table .ant-table-thead > tr > th {
      background:${tokens.panel2} !important; font-size:11.5px; text-transform:uppercase;
      letter-spacing:.05em; font-weight:700; color:${tokens.text3} !important;
    }
    .tg-admin-root .tg-rev-table .ant-table-tbody > tr > td { padding:12px 16px !important; }
    .tg-admin-root .tg-rev-table .ant-table-tbody > tr { cursor:pointer; }
    .tg-admin-root .tg-rev-table .tg-rev-row-overdue > td { background:${isDark ? 'rgba(240,115,108,.07)' : '#fff6f5'} !important; }
    /* ApexCharts tooltips: force our own surface + text colour so hovering a
       chart never renders dark text on a saturated series-coloured chip. */
    .tg-admin-root .apexcharts-tooltip,
    .tg-admin-root .apexcharts-tooltip.apexcharts-theme-light,
    .tg-admin-root .apexcharts-tooltip.apexcharts-theme-dark {
      background: ${tokens.panel} !important;
      border: 1px solid ${tokens.border2} !important;
      box-shadow: ${tokens.shSoft} !important;
      border-radius: 12px !important;
      color: ${tokens.text} !important;
    }
    .tg-admin-root .apexcharts-tooltip-title {
      background: ${tokens.panel2} !important;
      border-bottom: 1px solid ${tokens.hair} !important;
      color: ${tokens.text2} !important;
      font-weight: 600 !important;
    }
    .tg-admin-root .apexcharts-tooltip-text,
    .tg-admin-root .apexcharts-tooltip-text-y-label,
    .tg-admin-root .apexcharts-tooltip-text-y-value,
    .tg-admin-root .apexcharts-datalabel-label,
    .tg-admin-root .apexcharts-datalabel-value { color: ${tokens.text} !important; }
    .tg-admin-root .apexcharts-tooltip-y-group { padding: 3px 0 !important; }
    .tg-admin-root .apexcharts-legend-text { color: ${tokens.text2} !important; }
    .tg-admin-root .apexcharts-tooltip-marker { margin-right: 7px !important; }

    .tg-rev-foot { font-size:11.5px; color:${tokens.text3}; display:flex; gap:14px; flex-wrap:wrap; align-items:center; }

    /* Responsive panel grids — collapse to one column on narrow screens. */
    .tg-rev-grid-2 { display:grid; grid-template-columns:minmax(0,2fr) minmax(0,1fr); gap:16px; }
    .tg-rev-grid-3 { display:grid; grid-template-columns:repeat(3, minmax(0,1fr)); gap:16px; }
    @media (max-width: 1280px) {
      .tg-rev-grid-3 { grid-template-columns:repeat(2, minmax(0,1fr)); }
    }
    @media (max-width: 1024px) {
      .tg-rev-grid-2, .tg-rev-grid-3 { grid-template-columns:minmax(0,1fr); }
    }
  `;

  /* ------------------------------------------------------------------ views */

  const OverviewTab = (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="tg-rev-grid-2">
        <Panel
          title="Revenue collected"
          subtitle={`Net of refunds, stacked by gateway · ${dateText(dates.from)} → ${dateText(dates.to)}`}
        >
          {loadingOverview ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : series.length ? (
            <ReactApexChart options={trendOptions} series={trendSeries} type="bar" height={300} />
          ) : (
            <Empty description="No payments in this window" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Panel>

        <Panel title="MRR composition" subtitle="Where the recurring money is mandated">
          {loadingOverview ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : mrr.total ? (
            <>
              <ReactApexChart options={mrrDonutOptions} series={mrrDonutSeries} type="donut" height={270} />
              {mrr.manual > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 11.5,
                    color: tokens.warnTx,
                    background: isDark ? 'rgba(224,176,74,.10)' : '#fff8e8',
                    border: `1px solid ${isDark ? 'rgba(224,176,74,.25)' : '#f5e3bb'}`,
                    borderRadius: 12,
                    padding: '9px 12px',
                    lineHeight: 1.45,
                  }}
                >
                  <b>{money(inCur(mrr.manual), currency, { compact: true })}</b> of MRR has no gateway mandate behind it —
                  those renewals must be collected by hand.
                </div>
              )}
            </>
          ) : (
            <Empty description="No active subscriptions" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Panel>
      </div>

      <div className="tg-rev-grid-3">
        <Panel title="Revenue by plan" subtitle="MRR contribution of each plan">
          {loadingOverview ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : planMix.length ? (
            <div>
              {planMix.map((p) => {
                const max = planMix[0].mrrUsd || 1;
                return (
                  <div className="tg-rev-plan" key={p.name}>
                    <span className="tg-rev-plan-name" title={p.name}>
                      {p.name}
                    </span>
                    <span className="tg-rev-plan-bar">
                      <span className="tg-rev-plan-fill" style={{ width: `${Math.max(4, (p.mrrUsd / max) * 100)}%` }} />
                    </span>
                    <span className="tg-rev-plan-val">{money(inCur(p.mrrUsd), currency, { compact: true })}</span>
                  </div>
                );
              })}
              <div style={{ marginTop: 10, fontSize: 11.5, color: tokens.text3 }}>
                {planMix.reduce((a, p) => a + p.count, 0)} subscriptions · {planMix.reduce((a, p) => a + p.seats, 0)} seats
              </div>
            </div>
          ) : (
            <Empty description="No plan data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Panel>

        <Panel title="Money at risk" subtitle="Where revenue leaks today">
          {loadingOverview ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <div>
              {[
                { label: 'Past-due subscriptions', value: risk.pastDue, tone: '#e5514b', icon: AlertTriangle },
                { label: 'Lapsed manual renewals', value: risk.overdueManual, tone: '#e0a020', icon: CalendarClock, hint: 'Still marked active but past their period end — service given away free.' },
                { label: 'Active without a mandate', value: risk.noMandate, tone: '#e0a020', icon: ShieldAlert, hint: 'Nothing auto-charges these orgs; every cycle needs a human.' },
                { label: 'Orphaned subs still charging', value: risk.orphanedSubs, tone: '#e5514b', icon: Building2, hint: 'Live at the gateway with a TeamGrid org id whose organization was deleted — a real card is still being charged every cycle. Historical (already-cancelled) orphans are not counted here.' },
                { label: 'Failed payments in window', value: risk.failedPayments, tone: '#e5514b', icon: CreditCard },
                { label: 'Disputes / chargebacks', value: risk.disputed, tone: '#e5514b', icon: ShieldAlert },
              ].map((r) => (
                <div className="tg-rev-risk" key={r.label}>
                  <span className="tg-rev-risk-l">
                    <r.icon size={15} style={{ color: r.value ? r.tone : tokens.text3 }} />
                    {r.hint ? (
                      <AntTooltip title={r.hint}>
                        <span style={{ borderBottom: `1px dotted ${tokens.border2}`, cursor: 'help' }}>{r.label}</span>
                      </AntTooltip>
                    ) : (
                      r.label
                    )}
                  </span>
                  <span className="tg-rev-risk-v" style={{ color: r.value ? r.tone : tokens.text3 }}>
                    {r.value || 0}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Collection quality" subtitle="How reliably money arrives">
          {loadingOverview ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: tokens.text2, marginBottom: 6 }}>
                  <span>Payment success rate</span>
                  <b style={{ color: tokens.text }}>{(collected.successRate || 0).toFixed(1)}%</b>
                </div>
                <Progress
                  percent={Number((collected.successRate || 0).toFixed(1))}
                  showInfo={false}
                  strokeColor={{ from: tokens.accent, to: tokens.accent2 }}
                  trailColor={tokens.hair}
                />
              </div>
              {[
                ['Gross collected', money(inCur(collected.grossUsd), currency)],
                ['Refunded', money(inCur(collected.refundUsd), currency)],
                ['Net collected', money(inCur(collected.netUsd), currency)],
                ['Value of failed attempts', money(inCur(collected.failedUsd), currency)],
                ['Stripe share', money(inCur(collected.byGateway?.stripe), currency)],
                ['Razorpay share', money(inCur(collected.byGateway?.razorpay), currency)],
                ['Unattributed payments', `${counts.unattributedPayments || 0}`],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: tokens.text2 }}>
                  <span>{k}</span>
                  <b style={{ color: tokens.text }}>{v}</b>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* ---- retention & churn -------------------------------------------- */}
      <div className="tg-rev-grid-2">
        <Panel
          title="Retention & churn"
          subtitle="Customers gained and lost each month, with the share retained — always the full 12 months, not the range above"
        >
          {!ret ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : retSeries.some((m) => m.startCount > 0) ? (
            <>
              <ReactApexChart options={retentionOptions} series={retentionChartSeries} type="line" height={280} />
              <div style={{ fontSize: 11, color: tokens.text3, marginTop: 8, lineHeight: 1.5 }}>{ret.note}</div>
            </>
          ) : (
            <Empty description="Not enough payment history yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Panel>

        <Panel title="Churn at a glance" subtitle="Latest complete month, and the trailing average">
          {!ret ? (
            <Skeleton active paragraph={{ rows: 5 }} />
          ) : (
            <div style={{ display: 'grid', gap: 14 }}>
              {[
                {
                  label: 'Customer churn',
                  hint: 'Share of paying customers who stopped paying, counting every customer equally — regardless of what they paid. ("Logo churn" in SaaS jargon.)',
                  latest: ret.latest?.logoChurnPct,
                  avg: ret.averages?.logoChurnPct,
                  detail: ret.latest ? `${ret.latest.churnedCount} of ${ret.latest.startCount} customers` : null,
                  invert: true,
                },
                {
                  label: 'Revenue churn',
                  hint: 'Share of MRR lost to customers who stopped paying, weighted by what each was paying. Lower than customer churn means the ones you lost were smaller than average.',
                  latest: ret.latest?.grossRevenueChurnPct,
                  avg: ret.averages?.grossRevenueChurnPct,
                  detail: ret.latest ? `${money(inCur(ret.latest.churnedMrrUsd), currency)} lost` : null,
                  invert: true,
                },
                {
                  label: 'Net revenue retention',
                  hint: 'What last month’s customers are worth today, including expansion. Above 100% means the base grew on its own.',
                  latest: ret.latest?.nrrPct,
                  avg: ret.averages?.nrrPct,
                  detail: ret.latest ? `${ret.latest.retainedCount} retained · ${ret.latest.newCount} new` : null,
                  invert: false,
                },
              ].map((r) => {
                const v = Number(r.latest || 0);
                const good = r.invert ? v <= 5 : v >= 100;
                const colour = r.latest == null ? tokens.text3 : good ? tokens.okTx : tokens.badTx;
                return (
                  <div key={r.label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                      <AntTooltip title={r.hint}>
                        <span style={{ fontSize: 12.5, color: tokens.text2, borderBottom: `1px dotted ${tokens.border2}`, cursor: 'help' }}>
                          {r.label}
                        </span>
                      </AntTooltip>
                      <b style={{ fontSize: 20, fontWeight: 800, color: colour, letterSpacing: '-0.02em' }}>
                        {r.latest == null ? '—' : `${v.toFixed(1)}%`}
                      </b>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: tokens.text3, marginTop: 3 }}>
                      <span>{r.detail || ''}</span>
                      <span>{ret.averages?.months ? `${Number(r.avg || 0).toFixed(1)}% avg over ${ret.averages.months}m` : ''}</span>
                    </div>
                  </div>
                );
              })}

              {counts.payingOrgs > 0 && counts.payingOrgs < 30 && (
                <div
                  style={{
                    fontSize: 11.5,
                    color: tokens.warnTx,
                    background: isDark ? 'rgba(224,176,74,.10)' : '#fff8e8',
                    border: `1px solid ${isDark ? 'rgba(224,176,74,.25)' : '#f5e3bb'}`,
                    borderRadius: 12,
                    padding: '9px 12px',
                    lineHeight: 1.45,
                  }}
                >
                  With {counts.payingOrgs} paying customers, one loss moves customer churn by{' '}
                  <b>{(100 / counts.payingOrgs).toFixed(1)}%</b>. Read the counts, not the percentage.
                </div>
              )}
            </div>
          )}
        </Panel>
      </div>

      {/* ---- who left, and why --------------------------------------------- */}
      <Panel
        title="Churned customers"
        subtitle={
          retention
            ? `${retention.churnedList?.length || 0} left in the last 12 months · ${
                retention.notesRecorded || 0
              } with a recorded reason`
            : 'Loading…'
        }
        right={
          retention?.churnedList?.length ? (
            <AntButton size="small" icon={<Download size={14} />} onClick={() => exportCsv('churned')}>
              Export
            </AntButton>
          ) : null
        }
      >
        <div className="tg-rev-table">
          <Table
            dataSource={retention?.churnedList || []}
            columns={churnedColumns}
            rowKey={(r) => `${r.orgId}:${r.period}`}
            size="middle"
            loading={!retention}
            pagination={
              (retention?.churnedList?.length || 0) > 10
                ? { pageSize: 10, showTotal: (t) => `${t} churned customers` }
                : false
            }
            scroll={{ x: 900 }}
            locale={{
              emptyText: <Empty description="Nobody has churned in the last 12 months" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
            }}
          />
        </div>
      </Panel>

      {/* ---- usage-based churn risk ---------------------------------------- */}
      <Panel
        title="Paying but not using"
        subtitle={
          atRisk
            ? `${atRisk.totals.count} subscription${atRisk.totals.count === 1 ? '' : 's'} · ${money(
                inCur(atRisk.totals.mrrAtRiskUsd),
                currency
              )} MRR at risk · usage measured over the last ${atRisk.totals.usageWindowDays} days, desktop app and background agent combined`
            : 'Checking product usage against paid seats…'
        }
        right={
          atRisk?.rows?.length ? (
            <AntButton size="small" icon={<Download size={14} />} onClick={() => exportCsv('at-risk')}>
              Export
            </AntButton>
          ) : null
        }
      >
        <div className="tg-rev-table">
          <Table
            dataSource={atRisk?.rows || []}
            columns={atRiskColumns}
            rowKey="orgId"
            size="middle"
            loading={!retention}
            pagination={false}
            scroll={{ x: 1000 }}
            locale={{
              emptyText: (
                <Empty
                  description="Every paying customer has active users — nothing at risk"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        </div>
      </Panel>

      <Panel
        title="Latest payments"
        subtitle="Most recent money in, across both gateways"
        right={
          <AntButton size="small" onClick={() => onTabChange('payments')}>
            View all
          </AntButton>
        }
      >
        <div className="tg-rev-table">
          <Table
            dataSource={overview?.recentPayments || []}
            columns={paymentColumns}
            rowKey="key"
            size="middle"
            pagination={false}
            loading={loadingOverview}
            scroll={{ x: 1100 }}
            onRow={(r) => ({ onClick: () => setDetail({ type: 'payment', row: r }) })}
            locale={{ emptyText: <Empty description="No payments yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        </div>
      </Panel>
    </div>
  );

  const PaymentsTab = (
    <Panel
      title="Payment history"
      subtitle={`${payments.total || 0} payments · ${money(inCur(payments.totals?.netUsd), currency)} net in this view`}
      right={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Input.Search
            allowClear
            placeholder="Search id, org, email, method…"
            style={{ width: 260 }}
            defaultValue={payFilters.q}
            onSearch={(v) => {
              const next = { ...payFilters, q: v, page: 1 };
              setPayFilters(next);
              fetchPayments(false, next);
            }}
          />
          <Select
            allowClear
            placeholder="Gateway"
            style={{ width: 130 }}
            value={payFilters.gateway}
            options={[
              { value: 'stripe', label: 'Stripe' },
              { value: 'razorpay', label: 'Razorpay' },
            ]}
            onChange={(v) => {
              const next = { ...payFilters, gateway: v || null, page: 1 };
              setPayFilters(next);
              fetchPayments(false, next);
            }}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 150 }}
            value={payFilters.status}
            options={[
              { value: 'succeeded', label: 'Succeeded' },
              { value: 'failed', label: 'Failed' },
              { value: 'pending', label: 'Pending' },
              { value: 'refunded', label: 'Refunded' },
              { value: 'partially_refunded', label: 'Partially refunded' },
            ]}
            onChange={(v) => {
              const next = { ...payFilters, status: v || null, page: 1 };
              setPayFilters(next);
              fetchPayments(false, next);
            }}
          />
        </div>
      }
    >
      <div className="tg-rev-table">
        <Table
          dataSource={payments.rows}
          columns={paymentColumns}
          rowKey="key"
          size="middle"
          loading={paymentsLoading}
          scroll={{ x: 1200 }}
          onRow={(r) => ({ onClick: () => setDetail({ type: 'payment', row: r }) })}
          pagination={{
            current: payFilters.page,
            pageSize: payFilters.pageSize,
            total: payments.total,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100, 200],
            showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}`,
            onChange: (page, pageSize) => {
              const next = { ...payFilters, page, pageSize };
              setPayFilters(next);
              fetchPayments(false, next);
            },
          }}
          locale={{ emptyText: <Empty description="No payments match these filters" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </div>
    </Panel>
  );

  const SubscriptionsTab = (
    <Panel
      title="Subscriptions"
      subtitle={`${subs.totals?.active || 0} active · ${subs.totals?.trialing || 0} trialing · ${subs.totals?.pastDue || 0} past due · ${money(inCur(subs.totals?.mrrUsd), currency)} MRR`}
      right={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Input.Search
            allowClear
            placeholder="Search org or subscription id…"
            style={{ width: 240 }}
            defaultValue={subFilters.q}
            onSearch={(v) => {
              const next = { ...subFilters, q: v };
              setSubFilters(next);
              fetchSubs(false, next);
            }}
          />
          <Select
            allowClear
            placeholder="Source"
            style={{ width: 140 }}
            value={subFilters.source}
            options={[
              { value: 'gateway', label: 'Gateway-backed' },
              { value: 'manual', label: 'Manual / one-time' },
            ]}
            onChange={(v) => {
              const next = { ...subFilters, source: v || null };
              setSubFilters(next);
              fetchSubs(false, next);
            }}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 140 }}
            value={subFilters.status}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'trialing', label: 'Trialing' },
              { value: 'past_due', label: 'Past due' },
              { value: 'canceled', label: 'Canceled' },
            ]}
            onChange={(v) => {
              const next = { ...subFilters, status: v || null };
              setSubFilters(next);
              fetchSubs(false, next);
            }}
          />
        </div>
      }
    >
      <div className="tg-rev-table">
        <Table
          dataSource={subs.rows}
          columns={subscriptionColumns}
          rowKey="key"
          size="middle"
          loading={subsLoading}
          scroll={{ x: 1300 }}
          onRow={(r) => ({ onClick: () => setDetail({ type: 'subscription', row: r }) })}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} subscriptions` }}
          locale={{ emptyText: <Empty description="No subscriptions" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </div>
    </Panel>
  );

  const UpcomingTab = (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="tg-rev-kpis">
        <KpiCard
          icon={CalendarClock}
          label="Next 7 days"
          tone="blue"
          isDark={isDark}
          value={money(inCur(upcoming.totals?.next7Usd), currency, { compact: true })}
          sub={`${upcoming.totals?.next7Count || 0} renewals`}
        />
        <KpiCard
          icon={CalendarClock}
          label="Next 30 days"
          tone="teal"
          isDark={isDark}
          value={money(inCur(upcoming.totals?.next30Usd), currency, { compact: true })}
          sub={`${upcoming.totals?.next30Count || 0} renewals`}
        />
        <KpiCard
          icon={Repeat}
          label="Auto-collecting"
          tone="green"
          isDark={isDark}
          value={money(inCur(upcoming.totals?.autoUsd), currency, { compact: true })}
          sub="mandate armed at the gateway"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Needs chasing"
          tone="orange"
          isDark={isDark}
          value={money(inCur(upcoming.totals?.manualUsd), currency, { compact: true })}
          sub="no mandate — invoice by hand"
        />
        <KpiCard
          icon={ShieldAlert}
          label="Already overdue"
          tone="red"
          isDark={isDark}
          value={money(inCur(upcoming.totals?.overdueUsd), currency, { compact: true })}
          sub={`${upcoming.totals?.overdueCount || 0} past their date`}
        />
      </div>

      <Panel
        title="Renewal calendar"
        subtitle="Every subscription due in the selected horizon, with the amount we expect to collect"
        right={
          <Segmented
            className="tg-rev-seg"
            value={upcomingDays}
            onChange={(v) => {
              setUpcomingDays(v);
              fetchUpcoming(false, v);
            }}
            options={[
              { label: '30 days', value: 30 },
              { label: '90 days', value: 90 },
              { label: '180 days', value: 180 },
              { label: '1 year', value: 365 },
            ]}
          />
        }
      >
        <div className="tg-rev-table">
          <Table
            dataSource={upcoming.rows}
            columns={upcomingColumns}
            rowKey="key"
            size="middle"
            loading={upcomingLoading}
            scroll={{ x: 1200 }}
            rowClassName={(r) => (r.overdue ? 'tg-rev-row-overdue' : '')}
            pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} renewals` }}
            locale={{ emptyText: <Empty description="Nothing due in this horizon" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        </div>
      </Panel>
    </div>
  );

  const CustomersTab = (
    <Panel
      title="Revenue by customer"
      subtitle={`${customers.rows?.length || 0} organizations · sorted by lifetime value`}
      right={
        <Input.Search
          allowClear
          placeholder="Search organization…"
          style={{ width: 260 }}
          defaultValue={customerQuery}
          onSearch={(v) => {
            setCustomerQuery(v);
            fetchCustomers(false, v);
          }}
        />
      }
    >
      <div className="tg-rev-table">
        <Table
          dataSource={customers.rows}
          columns={customerColumns}
          rowKey={(r) => r.orgId || r.orgName}
          size="middle"
          loading={customersLoading}
          scroll={{ x: 1300 }}
          onRow={(r) => ({ onClick: () => r.orgId && navigate(`/main-admin?tab=organizations&orgId=${r.orgId}`) })}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} organizations` }}
          locale={{ emptyText: <Empty description="No customer revenue in this window" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </div>
    </Panel>
  );

  const SettlementsTab = (
    <Panel
      title="Razorpay settlements"
      subtitle={`Money that actually reached the bank · ${money(settlements.totals?.amount, 'INR')} settled, ${money(
        settlements.totals?.fees,
        'INR'
      )} in fees`}
    >
      <div className="tg-rev-table">
        <Table
          dataSource={settlements.rows}
          columns={settlementColumns}
          rowKey="key"
          size="middle"
          loading={settlementsLoading}
          scroll={{ x: 1000 }}
          pagination={{ pageSize: 25, showTotal: (t) => `${t} settlements` }}
          locale={{
            emptyText: (
              <Empty
                description="No settlements in this window (or the API key lacks settlement access)"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>
    </Panel>
  );

  /* ------------------------------------------------------------------ render */

  const sources = meta?.sources || [];

  return (
    <>
      <style>{designSystem}</style>
      <style>{pageCss}</style>
      <AdminThemeProvider>
        <div className={`${ADMIN_ROOT} h-screen flex overflow-hidden`}>
          <div className="tg-shell">
            <AdminSidebar activeKey="revenue" />

            <div className="flex flex-col w-0 flex-1 overflow-hidden">
              <main className="tg-rev-scroll flex-1 relative z-0 overflow-y-auto focus:outline-none">
                <div className="tg-rev-page py-6 px-5 sm:px-6 w-full">
                  {/* Header */}
                  <div className="tg-rev-head">
                    <div className="tg-rev-head-title">
                      <h1 style={{ fontSize: 24, fontWeight: 800, color: tokens.text, letterSpacing: '-0.03em', margin: 0 }}>
                        Revenue
                      </h1>
                      <p style={{ fontSize: 13, color: tokens.text3, margin: '4px 0 0' }}>
                        Every payment, subscription and renewal — Stripe, Razorpay and manual billing in one ledger.
                      </p>
                    </div>

                    <div className="tg-rev-controls">
                      <Segmented
                        className="tg-rev-seg"
                        value={range}
                        onChange={(v) => {
                          setRange(v);
                          if (v !== 'custom') setCustomRange(null);
                        }}
                        options={RANGES}
                        size="middle"
                      />
                      {range === 'custom' && (
                        <DatePicker.RangePicker
                          value={customRange}
                          onChange={setCustomRange}
                          allowClear={false}
                          allowEmpty={[false, false]}
                        />
                      )}
                      {/* Labels only. Passing `icon` alongside `label` makes
                          AntD stack the glyph above the text, which wrapped the
                          toggle onto two lines ("$" over "USD"). */}
                      <Segmented
                        className="tg-rev-seg"
                        value={currency}
                        onChange={setCurrency}
                        options={[
                          { label: '$ USD', value: 'USD' },
                          { label: '₹ INR', value: 'INR' },
                        ]}
                      />
                      <AntTooltip title="Re-pull everything live from Stripe and Razorpay">
                        <AntButton
                          icon={<RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />}
                          onClick={refreshAll}
                          loading={false}
                          disabled={refreshing}
                        >
                          Refresh
                        </AntButton>
                      </AntTooltip>
                      <AntButton type="primary" icon={<Download size={15} />} onClick={exportCsv}>
                        Export CSV
                      </AntButton>
                    </div>
                  </div>

                  {/* Gateway status strip */}
                  {sources.length === 0 ? (
                    <div className="tg-rev-srcrow">
                      <span className="tg-rev-src sk-pill" />
                      <span className="tg-rev-src sk-pill" />
                    </div>
                  ) : (
                    <div className="tg-rev-srcrow">
                      {/* {sources.map((s) => (
                        <span className="tg-rev-src" key={s.gateway}>
                          <span
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: 999,
                              background: s.ok ? tokens.dotOk : tokens.dotBad,
                              display: 'inline-block',
                            }}
                          />
                          <b style={{ color: tokens.text, textTransform: 'capitalize' }}>{s.gateway}</b>
                          {s.ok ? (
                            <span style={{ color: tokens.text3 }}>
                              {s.mode === 'test' ? 'TEST · ' : ''}
                              {s.payments} payments · {s.subscriptions} subs
                              {s.truncated ? (
                                <AntTooltip title="This window has more payments than one read can return, so the totals below are incomplete. Narrow the date range, or move the ledger to local storage.">
                                  <b style={{ color: tokens.badTx, marginLeft: 6 }}>· incomplete</b>
                                </AntTooltip>
                              ) : null}
                            </span>
                          ) : (
                            <AntTooltip title={s.error}>
                              <span style={{ color: tokens.badTx, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                unavailable — {s.error}
                              </span>
                            </AntTooltip>
                          )}
                        </span>
                      ))} */}
                      {meta?.cached ? (
                        <span style={{ fontSize: 11.5, color: tokens.text3 }}>
                          cached at {dayjs(meta.builtAt).format('HH:mm')} — hit Refresh for live
                        </span>
                      ) : null}
                    </div>
                  )}

                  {/* Shared-merchant-account guard. The Razorpay account also
                      serves another product, so anything without a TeamGrid
                      organizationId is held out of every figure on this page. */}
                  {excluded?.payments || excluded?.subscriptions ? (
                    <div className="tg-rev-note">
                      <Building2 size={15} style={{ color: tokens.text3, flex: 'none' }} />
                      <span className="tg-rev-note-text">
                        Showing <b style={{ color: tokens.text }}>TeamGrid revenue only</b>. Held out of every figure:{' '}
                        <b style={{ color: tokens.text }}>{excluded.payments}</b> payment
                        {excluded.payments === 1 ? '' : 's'} ({money(inCur(excluded.netUsd), currency, { compact: true })})
                        {excluded.subscriptions
                          ? ` and ${excluded.subscriptions} subscription${excluded.subscriptions === 1 ? '' : 's'} (${money(
                              inCur(excluded.mrrUsd),
                              currency,
                              { compact: true }
                            )} MRR)`
                          : ''}{' '}
                        with no TeamGrid organization — another product on the same merchant account.
                      </span>
                      <Segmented
                        className="tg-rev-seg"
                        value={scope}
                        onChange={(v) => {
                          setScope(v);
                          // The ref is what the fetchers read, and it is only
                          // refreshed on render — set it here so the request
                          // below carries the NEW scope, not the previous one.
                          scopeRef.current = v;
                          // Scope only changes the two list endpoints, so reload
                          // whichever of them is open.
                          if (tab === 'payments') fetchPayments(false, { ...payFilters, page: 1 });
                          else if (tab === 'subscriptions') fetchSubs(false, subFilters);
                          else onTabChange('payments');
                        }}
                        options={[
                          { label: 'TeamGrid', value: 'teamgrid' },
                          { label: 'Other products', value: 'foreign' },
                          { label: 'All', value: 'all' },
                        ]}
                      />
                    </div>
                  ) : null}

                  {/* A cold build pulls every charge from both gateways over
                      cursor-paginated pages — ~5s for 30 days, ~50s for All.
                      Say so, rather than letting a long spinner look hung. */}
                  {loadingOverview && !overview ? (
                    <div className="tg-rev-note">
                      <RefreshCw size={15} className="animate-spin" style={{ color: tokens.text3, flex: 'none' }} />
                      <span className="tg-rev-note-text">
                        Pulling live data from Stripe and Razorpay…
                        {range === 'all'
                          ? ' the All range reads every charge ever and can take about a minute.'
                          : ' this takes a few seconds.'}{' '}
                        Results are then cached for 10 minutes.
                      </span>
                    </div>
                  ) : null}

                  {error ? (
                    <div
                      style={{
                        border: `1px solid ${isDark ? 'rgba(240,115,108,.3)' : '#f5cfcd'}`,
                        background: isDark ? 'rgba(240,115,108,.10)' : '#fdecea',
                        color: tokens.badTx,
                        borderRadius: 14,
                        padding: '12px 16px',
                        fontSize: 13,
                      }}
                    >
                      {error}
                    </div>
                  ) : null}

                  {/* KPIs */}
                  <div className="tg-rev-kpis">
                    {loadingOverview && !overview
                      ? KPI_SLOTS.map((k) => <KpiCard key={k.label} {...k} isDark={isDark} loading />)
                      : kpis.map((k) => <KpiCard key={k.label} {...k} isDark={isDark} />)}
                  </div>

                  {/* Tabs */}
                  <Tabs
                    activeKey={tab}
                    onChange={onTabChange}
                    // The ink bar may glide; the PANES must not. AntD slides the
                    // pane container horizontally by default, which reads as the
                    // whole page sweeping left-to-right on every tab change.
                    animated={{ inkBar: true, tabPane: false }}
                    items={[
                      { key: 'overview', label: 'Overview', children: OverviewTab },
                      { key: 'payments', label: 'Payment history', children: PaymentsTab },
                      { key: 'subscriptions', label: 'Subscriptions', children: SubscriptionsTab },
                      { key: 'upcoming', label: 'Upcoming billing', children: UpcomingTab },
                      { key: 'customers', label: 'Customers', children: CustomersTab },
                      { key: 'settlements', label: 'Settlements', children: SettlementsTab },
                    ]}
                  />

                  {/* Footer provenance — never let a converted number look booked */}
                  <div className="tg-rev-foot">
                    <span>
                      FX 1 USD = ₹{(meta?.fx?.usdInr || 87).toFixed(2)} ({meta?.fx?.source || 'default'})
                    </span>
                    <span>·</span>
                    <span>Window {dateText(dates.from)} → {dateText(dates.to)}</span>
                    <span>·</span>
                    <span>Built {meta?.builtAt ? dayjs(meta.builtAt).format('DD MMM, HH:mm:ss') : '—'} in {meta?.buildMs || 0}ms</span>
                    {meta?.stripeMode === 'test' ? (
                      <>
                        <span>·</span>
                        <b style={{ color: tokens.warnTx }}>Stripe is in TEST mode</b>
                      </>
                    ) : null}
                  </div>
                </div>
              </main>
            </div>
          </div>
        </div>

        {/* Why a customer left — the one thing the gateways cannot tell us. */}
        <Modal
          open={!!noteFor}
          onCancel={() => setNoteFor(null)}
          title={noteFor ? `Why did ${noteFor.orgName} leave?` : ''}
          okText={noteSaving ? 'Saving…' : 'Save reason'}
          onOk={saveNote}
          confirmLoading={noteSaving}
          destroyOnClose
          footer={(_, { OkBtn, CancelBtn }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {noteFor?.note?._id ? (
                <AntButton danger onClick={deleteNote} disabled={noteSaving} style={{ marginRight: 'auto' }}>
                  Remove
                </AntButton>
              ) : null}
              <CancelBtn />
              <OkBtn />
            </div>
          )}
        >
          {noteFor ? (
            <div style={{ display: 'grid', gap: 14, paddingTop: 4 }}>
              <div style={{ fontSize: 12.5, color: tokens.text3 }}>
                {noteFor.period ? `Stopped paying in ${dayjs(`${noteFor.period}-01`).format('MMMM YYYY')}` : 'Account note'}
                {noteFor.mrrLostUsd ? ` · ${money(inCur(noteFor.mrrLostUsd), currency)} MRR lost` : ''}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: tokens.text2, marginBottom: 6 }}>
                  Reason <span style={{ color: '#e5514b' }}>*</span>
                </label>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Pick the main reason"
                  value={noteDraft.reason}
                  onChange={(v) => setNoteDraft((d) => ({ ...d, reason: v }))}
                  options={(retention?.reasons || Object.entries(REASON_LABELS).map(([value, label]) => ({ value, label })))}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: tokens.text2, marginBottom: 6 }}>
                  What happened
                </label>
                <Input.TextArea
                  rows={4}
                  maxLength={2000}
                  showCount
                  placeholder="What did they actually say? Anything we could have done?"
                  value={noteDraft.note}
                  onChange={(e) => setNoteDraft((d) => ({ ...d, note: e.target.value }))}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: tokens.text2, marginBottom: 6 }}>
                  Winnable back?
                </label>
                <Segmented
                  className="tg-rev-seg"
                  value={noteDraft.recoverable === null ? 'unknown' : noteDraft.recoverable ? 'yes' : 'no'}
                  onChange={(v) => setNoteDraft((d) => ({ ...d, recoverable: v === 'unknown' ? null : v === 'yes' }))}
                  options={[
                    { label: 'Unknown', value: 'unknown' },
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ]}
                />
              </div>
            </div>
          ) : null}
        </Modal>

        {/* Row detail */}
        <Drawer
          open={!!detail}
          onClose={() => setDetail(null)}
          width={520}
          title={detail?.type === 'payment' ? 'Payment detail' : 'Subscription detail'}
        >
          {detail?.row ? (
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <GatewayTag gateway={detail.row.gateway} source={detail.row.source} />
                <Pill status={detail.row.status} />
                {detail.row.dashboardUrl ? (
                  <a
                    href={detail.row.dashboardUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ marginLeft: 'auto', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  >
                    Open in dashboard <ExternalLink size={13} />
                  </a>
                ) : null}
              </div>

              <Descriptions column={1} size="small" bordered items={
                (detail.type === 'payment'
                  ? [
                      ['ID', detail.row.id],
                      ['Organization', detail.row.orgName || 'Unattributed'],
                      ['Org ID', detail.row.orgId],
                      ['Amount', money(detail.row.amount, detail.row.currency)],
                      ['Refunded', money(detail.row.refunded, detail.row.currency)],
                      ['Net', money(detail.row.net, detail.row.currency)],
                      ['Net (USD)', money(detail.row.netUsd, 'USD')],
                      ['Net (INR)', money(detail.row.netInr, 'INR')],
                      ['Method', detail.row.method],
                      ['Email', detail.row.email],
                      ['Contact', detail.row.contact],
                      ['Description', detail.row.description],
                      ['Created', dateTimeText(detail.row.createdAt)],
                      ['Customer ID', detail.row.customerId],
                      ['Order ID', detail.row.orderId],
                      ['Invoice ID', detail.row.invoiceId],
                      ['Gateway fee', detail.row.fee != null ? money(detail.row.fee, detail.row.currency) : null],
                      ['Failure', detail.row.failureMessage],
                      ['Raw status', detail.row.rawStatus],
                    ]
                  : [
                      ['ID', detail.row.id],
                      ['Organization', detail.row.orgName || 'Unattributed'],
                      ['Org ID', detail.row.orgId],
                      ['Plan', detail.row.planName],
                      ['Seats', detail.row.seats],
                      ['Amount per cycle', money(detail.row.amount, detail.row.currency)],
                      ['Interval', `${detail.row.intervalCount || 1} × ${detail.row.interval}`],
                      ['MRR (USD)', money(detail.row.mrrUsd, 'USD')],
                      ['MRR (INR)', money(detail.row.mrrInr, 'INR')],
                      ['Auto-pay', detail.row.source === 'manual' ? 'Manual invoicing' : detail.row.autoPay ? 'On' : 'Off'],
                      ['Started', dateText(detail.row.startedAt)],
                      ['Current period', `${dateText(detail.row.currentPeriodStart)} → ${dateText(detail.row.currentPeriodEnd)}`],
                      ['Next charge', dateText(detail.row.nextChargeAt)],
                      ['Cycles paid', detail.row.paidCount != null ? `${detail.row.paidCount}${detail.row.totalCount ? ` / ${detail.row.totalCount}` : ''}` : null],
                      ['Customer ID', detail.row.customerId],
                      ['Raw status', detail.row.rawStatus],
                    ]
                )
                  .filter(([, v]) => v !== null && v !== undefined && v !== '')
                  .map(([label, value]) => ({ key: label, label, children: String(value) }))
              } />

              {detail.row.receiptUrl ? (
                <AntButton block href={detail.row.receiptUrl} target="_blank" rel="noreferrer">
                  View receipt
                </AntButton>
              ) : null}
              {detail.row.orgId ? (
                <AntButton
                  block
                  type="primary"
                  onClick={() => {
                    setDetail(null);
                    setTab('payments');
                    const next = { ...payFilters, q: detail.row.orgName || '', page: 1 };
                    setPayFilters(next);
                    loadedTabs.current.add('payments');
                    fetchPayments(false, next);
                  }}
                >
                  See all payments for this org
                </AntButton>
              ) : null}
            </div>
          ) : null}
        </Drawer>
      </AdminThemeProvider>
    </>
  );
};

export default RevenueDashboard;
